import { createGencowProcedureBuilders, type DocumentVisibility } from "@gencow/core";
import {
  loadAccessibleRagCorpusPolicy,
  parsePrivateExternalCorpusAllowlist,
  resolveAllowExternalProvidersPolicy,
} from "./rag-corpus-policy.js";
import { upsertRagCorpus } from "./rag-ingest-jobs.js";
import {
  RAG_BUILTIN_OPERATION_NAMES,
  ragBuiltinInputSchemas,
  ragBuiltinOutputSchemas,
} from "./builtins/rag/schemas.js";

const SUPPORTED_VISIBILITIES = new Set<DocumentVisibility>(["private", "shared", "public"]);
const procedure = createGencowProcedureBuilders();

function normalizeVisibility(value: string): DocumentVisibility {
  if (!SUPPORTED_VISIBILITIES.has(value as DocumentVisibility)) {
    throw new Error(`Unsupported visibility "${value}"`);
  }
  return value as DocumentVisibility;
}

function normalizeMetadata(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }
  return value as Record<string, unknown>;
}

function resolveOwnerUserId(input: {
  visibility: DocumentVisibility;
  ownerUserId?: string;
  viewerUserId: string;
}): string | null {
  if (input.visibility !== "private") {
    return input.ownerUserId?.trim() || null;
  }

  const requestedOwner = input.ownerUserId?.trim();
  if (requestedOwner && requestedOwner !== input.viewerUserId) {
    throw new Error("ownerUserId must match the authenticated user for private corpus policy");
  }
  return requestedOwner ?? input.viewerUserId;
}

function mapCorpusPolicy(row: {
  corpus: string;
  visibility_scope: DocumentVisibility;
  owner_user_id: string | null;
  allow_external_providers: boolean;
  metadata: unknown;
}) {
  return {
    corpus: row.corpus,
    visibility: row.visibility_scope,
    ownerUserId: row.owner_user_id,
    allowExternalProviders: !!row.allow_external_providers,
    metadata: normalizeMetadata(row.metadata),
  };
}

export const ragCorporaProcedures = {
  get: procedure.query
    .name(RAG_BUILTIN_OPERATION_NAMES.ragCorporaGet)
    .input(ragBuiltinInputSchemas.ragCorporaGet)
    .output(ragBuiltinOutputSchemas.ragCorporaGet)
    .handler(async ({ context: ctx, input: rawArgs }) => {
      const viewer = ctx.auth.requireAuth();
      const visibility = normalizeVisibility(rawArgs.visibility);
      const ownerUserId = resolveOwnerUserId({
        visibility,
        ownerUserId: rawArgs.ownerUserId?.trim() || undefined,
        viewerUserId: viewer.id,
      });
      const row = await loadAccessibleRagCorpusPolicy(ctx.unsafeDb, {
        corpus: rawArgs.corpus.trim(),
        visibility,
        ownerUserId,
        viewerUserId: viewer.id,
      });

      if (row) {
        return {
          ...mapCorpusPolicy(row),
          exists: true,
        };
      }

      return {
        corpus: rawArgs.corpus.trim(),
        visibility,
        ownerUserId,
        allowExternalProviders: await resolveAllowExternalProvidersPolicy({
          db: ctx.unsafeDb,
          corpus: rawArgs.corpus.trim(),
          visibility,
          ownerUserId,
          allowPrivateExternalCorpora: parsePrivateExternalCorpusAllowlist(
            process.env.GENCOW_DOCUMENT_EXTERNAL_PRIVATE_CORPORA,
          ),
        }),
        metadata: {},
        exists: false,
      };
    }),
  setPolicy: procedure.mutation
    .name(RAG_BUILTIN_OPERATION_NAMES.ragCorporaSetPolicy)
    .input(ragBuiltinInputSchemas.ragCorporaSetPolicy)
    .output(ragBuiltinOutputSchemas.ragCorporaSetPolicy)
    .handler(async ({ context: ctx, input: rawArgs }) => {
      const viewer = ctx.auth.requireAuth();
      const visibility = normalizeVisibility(rawArgs.visibility);
      const ownerUserId = resolveOwnerUserId({
        visibility,
        ownerUserId: rawArgs.ownerUserId?.trim() || undefined,
        viewerUserId: viewer.id,
      });
      const metadata = normalizeMetadata(rawArgs.metadata);

      await upsertRagCorpus(ctx.unsafeDb, {
        corpus: rawArgs.corpus.trim(),
        visibility,
        ownerUserId,
        allowExternalProviders: rawArgs.allowExternalProviders,
        metadata,
      });

      return {
        corpus: rawArgs.corpus.trim(),
        visibility,
        ownerUserId,
        allowExternalProviders: rawArgs.allowExternalProviders,
        metadata,
        exists: true,
      };
    }),
} as const;

export function registerRagCorporaRuntime(): void {}
